import { LEVELS } from '@dc1/shared';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AreaView, ContentView } from '../../lib/content';
import { readPref, writePref } from '../../lib/ui-prefs';
import { Segmented } from '../../ui/Segmented';
import s from './dashboard.module.css';
import { type AreaCell, areaCell, type DashMember, levelForScore } from './dashboard-model';

type HeatMode = 'groups' | 'areas' | 'table';

interface HeatmapProps {
  members: readonly DashMember[];
  meId: string;
  content: ContentView;
  onOpen: (memberId: string) => void;
  /** Inside the dashboard's detail sheet: no card, the sheet's title stands for the heading. */
  bare?: boolean;
}

/**
 * Look of one person × area cell, as data-kind + CSS variables: a bar as tall as the self level in its
 * heatmap colour, hatched with the level that counts while it waits for confirmation.
 */
function cellLook(cell: AreaCell): { kind: string; style?: CSSProperties } {
  if (cell.kind !== 'level') return { kind: cell.kind };
  const style = {
    '--lv': `var(--heatmap-${cell.self})`,
    '--lb': `var(--heatmap-${cell.effective})`,
    '--h': `${(cell.self / LEVELS.length) * 100}%`,
  } as CSSProperties;
  return { kind: cell.pending ? 'pending' : 'level', style };
}

/**
 * "2. Bản đồ nhiệt người × mảng": by group, by area (selectable cells) or as a plain table.
 * Design v3 with the "bar" style and the "magma" palette (--heatmap-1…5).
 */
export function Heatmap({ members, meId, content, onOpen, bare }: HeatmapProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<HeatMode>(() => readPref('ui.heatmapMode') ?? 'groups');
  const [selected, setSelected] = useState<{ memberId: string; areaId: string } | null>(null);
  const areas = content.groups.flatMap((g) => g.areas);

  const change = (m: HeatMode) => {
    setMode(m);
    writePref('ui.heatmapMode', m);
  };

  const cellText = (a: AreaView, cell: AreaCell) => {
    if (cell.kind === 'na') return t('dashboard.cellNa', { area: a.id });
    if (cell.kind === 'none') return t('dashboard.cellNone', { area: a.id });
    const vars = { area: a.id, name: a.name, level: `L${cell.self}`, counted: `L${cell.effective}` };
    return cell.pending ? t('dashboard.cellPending', vars) : t('dashboard.cellLevel', vars);
  };

  const groupCols = { '--cols': content.groups.length } as CSSProperties;
  const areaCols = { '--cols': areas.length } as CSSProperties;

  return (
    <section className={`${s.card} ${s.padded}`} data-bare={bare} aria-labelledby="dash-heatmap">
      <div className={s.sectionHead}>
        <div className={bare ? 'visually-hidden' : s.sectionTitle}>
          <h2 id="dash-heatmap">{t('dashboard.heatmapTitle')}</h2>
          <p>{t('dashboard.heatmapNote')}</p>
        </div>
        <Segmented
          label={t('dashboard.heatmapView')}
          value={mode}
          options={[
            { value: 'groups', label: t('dashboard.byGroup', { n: content.groups.length }) },
            { value: 'areas', label: t('dashboard.byArea', { n: areas.length }) },
            { value: 'table', label: t('dashboard.asTable') },
          ]}
          onChange={change}
        />
      </div>

      {mode === 'groups' ? (
        <div className={s.scrollX}>
          <div className={s.heatGroups} role="table" aria-labelledby="dash-heatmap">
            <div className={`${s.heatRowGrid} ${s.heatHeadRow}`} role="row" style={groupCols}>
              <span role="columnheader">
                <span className="visually-hidden">{t('dashboard.member')}</span>
              </span>
              {content.groups.map((g) => (
                <span key={g.id} role="columnheader">
                  {g.short}
                </span>
              ))}
              <span role="columnheader" className={s.heatScoreHead}>
                {t('dashboard.score')}
              </span>
            </div>
            {members.map((m) => (
              <div key={m.id} className={s.heatRowGrid} role="row" style={groupCols}>
                <span role="rowheader">
                  <PersonButton member={m} meId={meId} content={content} onOpen={onOpen} />
                </span>
                {content.groups.map((g) => {
                  const score = m.groups.find((x) => x.groupId === g.id)?.effectiveScore ?? 0;
                  const level = levelForScore(score);
                  return (
                    <span key={g.id} role="cell" className={s.groupCell} data-zero={score === 0}>
                      <span className={s.groupNum}>{score}</span>
                      <span className={s.groupBar} aria-hidden="true">
                        <span
                          style={{ width: `${score}%`, '--lv': `var(--heatmap-${level})` } as CSSProperties}
                        />
                      </span>
                    </span>
                  );
                })}
                <span role="cell" className={s.heatScore}>
                  {m.effectiveScore}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {mode === 'table' ? (
        <div className={s.scrollX}>
          <div className={s.heatTable} role="table" aria-labelledby="dash-heatmap">
            <div className={`${s.heatRowGrid} ${s.tableHeadRow}`} role="row" style={groupCols}>
              <span role="columnheader">{t('dashboard.member')}</span>
              {content.groups.map((g) => (
                <span key={g.id} role="columnheader">
                  {g.short}
                </span>
              ))}
              <span role="columnheader" className={s.heatScoreHead}>
                {t('dashboard.score')}
              </span>
            </div>
            {members.map((m) => (
              <div key={m.id} className={`${s.heatRowGrid} ${s.tableRow}`} role="row" style={groupCols}>
                <span role="rowheader">
                  <PersonButton member={m} meId={meId} content={content} onOpen={onOpen} />
                </span>
                {content.groups.map((g) => (
                  <span key={g.id} role="cell" className={s.tableCell}>
                    {m.groups.find((x) => x.groupId === g.id)?.effectiveScore ?? 0}
                  </span>
                ))}
                <span role="cell" className={s.heatScore}>
                  {m.effectiveScore}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {mode === 'areas' ? (
        <>
          <div className={s.scrollX}>
            <div className={s.heatAreas}>
              <div className={s.heatAreaGrid} style={areaCols}>
                <span />
                {content.groups.map((g) => (
                  <span
                    key={g.id}
                    className={s.areaGroupHead}
                    style={{ gridColumn: `span ${g.areas.length}` }}
                    title={g.name}
                  >
                    {`${g.n}. ${g.short}`}
                  </span>
                ))}
              </div>
              <div className={s.heatAreaGrid} style={areaCols}>
                <span />
                {areas.map((a) => (
                  <span key={a.id} className={s.areaHead} title={`${a.id} ${a.name}`}>
                    {a.id}
                  </span>
                ))}
              </div>
              {members.map((m) => {
                const byArea = new Map(m.levels.map((l) => [l.areaId, l]));
                return (
                  <div key={m.id} className={s.heatAreaGrid} style={areaCols}>
                    <button
                      type="button"
                      className={s.heatAreaName}
                      data-me={m.id === meId}
                      onClick={() => {
                        onOpen(m.id);
                      }}
                    >
                      {m.displayName}
                    </button>
                    {areas.map((a) => {
                      const cell = areaCell(byArea.get(a.id));
                      const look = cellLook(cell);
                      const text = cellText(a, cell);
                      const on = selected?.memberId === m.id && selected.areaId === a.id;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          className={s.areaCell}
                          data-kind={look.kind}
                          style={look.style}
                          title={text}
                          aria-label={t('dashboard.cellFor', { person: m.displayName, cell: text })}
                          aria-pressed={on}
                          onClick={() => {
                            setSelected(on ? null : { memberId: m.id, areaId: a.id });
                          }}
                        >
                          {look.style ? <span className={s.areaFill} /> : null}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <Selection members={members} content={content} selected={selected} />
        </>
      ) : null}

      {mode === 'groups' ? (
        <div className={s.legendWrap}>
          <span className={s.legendScale}>
            0<span className={s.legendGradient} aria-hidden="true" />
            100
          </span>
          <span className={s.legendText}>{t('dashboard.legendGroups')}</span>
        </div>
      ) : null}
      {mode === 'areas' ? (
        <div className={s.legendWrap}>
          {LEVELS.map((l) => (
            <span key={l}>
              <span className={s.legendBox} style={{ '--lv': `var(--heatmap-${l})` } as CSSProperties} />
              {`L${l}`}
            </span>
          ))}
          <span>
            <span className={`${s.legendBox} ${s.legendPending}`} />
            {t('dashboard.legendPending')}
          </span>
          <span>
            <span className={`${s.legendBox} ${s.legendNa}`} />
            {t('dashboard.legendNa')}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function PersonButton({
  member,
  meId,
  content,
  onOpen,
}: {
  member: DashMember;
  meId: string;
  content: ContentView;
  onOpen: (memberId: string) => void;
  /** Inside the dashboard's detail sheet: no card, the sheet's title stands for the heading. */
  bare?: boolean;
}) {
  return (
    <button
      type="button"
      className={s.heatName}
      data-me={member.id === meId}
      title={member.displayName}
      onClick={() => {
        onOpen(member.id);
      }}
    >
      <span className={s.heatNameText}>{member.displayName}</span>
      <small>{content.ranks[member.rank].name}</small>
    </button>
  );
}

/** Read-out under the 39-area grid: who, which area, which level. */
function Selection({
  members,
  content,
  selected,
}: {
  members: readonly DashMember[];
  content: ContentView;
  selected: { memberId: string; areaId: string } | null;
}) {
  const { t } = useTranslation();
  const m = selected ? members.find((x) => x.id === selected.memberId) : undefined;
  const a = selected ? content.areaById.get(selected.areaId) : undefined;
  if (!m || !a) {
    return (
      <p className={s.selection} aria-live="polite">
        <span className={s.hint}>{t('dashboard.selectionHint')}</span>
      </p>
    );
  }
  const cell = areaCell(m.levels.find((l) => l.areaId === a.id));
  const look = cellLook(cell);
  const level =
    cell.kind === 'na'
      ? t('dashboard.selectionNa')
      : cell.kind === 'none'
        ? t('dashboard.selectionNone')
        : cell.pending
          ? t('dashboard.selectionPending', { level: `L${cell.self}`, counted: `L${cell.effective}` })
          : `L${cell.self}`;
  return (
    <p className={s.selection} aria-live="polite">
      <span className={s.selectionSwatch} data-kind={look.kind} style={look.style} aria-hidden="true" />
      <b>{m.displayName}</b>
      <span className={s.dotSep}>·</span>
      <span>{`${a.id} ${a.name}`}</span>
      <span className={s.dotSep}>·</span>
      <span className={s.mono600}>{level}</span>
    </p>
  );
}
